import currency from "currency.js";
import { parsePhoneNumberWithError } from "libphonenumber-js";

/**
 * Formats a numeric weight value with comma separators and optional unit.
 * Uses currency.js for precise number formatting without currency symbols.
 *
 * @param value - The weight value to format
 * @param unit - The unit to append (defaults to 'lb')
 * @returns Formatted weight string with unit or em dash for null values
 *
 * @example
 * // Format weight with default unit
 * formatWeight("49740.00") // => "49,740.00 lb"
 *
 * // Format weight with custom unit
 * formatWeight("100601.27", "kg") // => "100,601.27 kg"
 *
 * // Handle null/undefined
 * formatWeight(null) // => "—"
 */
export const formatWeight = (value: string | number | null | undefined, unit: "lb" | "kg" = "lb") => {
  if (value == null) return "\u2014";
  // Using Number constructor to handle scientific notation
  // https://github.com/scurker/currency.js/issues/412
  return `${currency(Number(value), { symbol: "" }).format()} ${unit}`;
};

/**
 * Formats a numeric value as currency with proper formatting and handling of null values.
 * Uses currency.js for precise currency formatting with dollar signs and comma separators.
 *
 * @param value - The numeric value to format as currency
 * @returns Formatted currency string or em dash for null values
 *
 * @example
 * // Format currency value
 * formatCurrency("84315.00") // => "$84,315.00"
 *
 * // Handle null/undefined
 * formatCurrency(null) // => "—"
 */
export const formatCurrency = (value: string | number | null | undefined) => {
  if (value == null) return "\u2014";
  return currency(value).format();
};

/**
 * Formats numeric values according to specified metric types with standardized formatting.
 * Handles percentage, currency, and weight conversions with appropriate units and decimal places.
 *
 * @param value - The string value to format
 * @param metricType - The type of metric formatting to apply ("percentage", "currency", or "weight")
 * @param fallbackLabel - Label to show when value is null/invalid (defaults to "N/A")
 * @returns Formatted string with appropriate units and decimal places
 *
 * @example
 * // Format as percentage
 * formatMetricValue({ value: "0.45", metricType: "percentage" }) // => "45.00%"
 *
 * // Format as currency
 * formatMetricValue({ value: "1234.5", metricType: "currency" }) // => "$1,234.50"
 *
 * // Format as weight
 * formatMetricValue({ value: "100.5", metricType: "weight" }) // => "100.50 lb"
 *
 * // Format without metric type
 * formatMetricValue({ value: "123.4" }) // => "123.40"
 */
export const formatMetricValue = ({
  metricType,
  value,
  fallbackLabel,
}: {
  value: string | null | undefined;
  metricType?: "percentage" | "currency" | "weight";
  fallbackLabel?: string;
}) => {
  if (!value) return fallbackLabel || "N/A";

  const numValue = parseFloat(value);

  if (isNaN(numValue)) return fallbackLabel || "N/A";

  switch (metricType) {
    case "percentage":
      return `${(numValue * 100).toFixed(2)}%`;
    case "currency":
      return formatCurrency(numValue);
    case "weight":
      return formatWeight(numValue);
    default:
      const [whole, decimal] = value.split(".");
      if (!decimal) return `${whole}.00`;
      return `${whole}.${decimal.padEnd(2, "0").slice(0, 2)}`;
  }
};

/**
 * Formats a full name from first and last name components
 * @param firstName - The person's first name
 * @param lastName - The person's last name
 * @param defaultValue - Value to display if name is incomplete, defaults to em dash
 * @returns Formatted full name or default value if incomplete
 */
export function formatName({
  firstName,
  lastName,
  defaultValue = "\u2014",
}: {
  firstName: Maybe<string>;
  lastName: Maybe<string>;
  defaultValue?: string;
}) {
  const trimmedFirst = firstName?.trim();
  const trimmedLast = lastName?.trim();

  return trimmedFirst && trimmedLast ? `${trimmedFirst} ${trimmedLast}` : defaultValue;
}

/**
 * Formats a business address into a single string
 * @param businessAddress - Object containing address components
 * @param businessAddress.line1 - First line of street address
 * @param businessAddress.line2 - Optional second line of street address
 * @param businessAddress.city - City name
 * @param businessAddress.state - State code
 * @param businessAddress.postcode - Postal code
 * @param defaultValue - Value to display if address is incomplete (defaults to em dash)
 * @returns Formatted address string or default value if incomplete
 */
export function formatAddress(
  businessAddress: Maybe<{
    line1?: Maybe<string>;
    line2?: Maybe<string>;
    city?: Maybe<string>;
    state?: Maybe<string>;
    postcode?: Maybe<string>;
  }>,
  defaultValue = "\u2014",
) {
  // Check if we have at least line1 and state - bare minimum for a meaningful address
  if (!businessAddress?.line1 || !businessAddress?.state) {
    return defaultValue;
  }

  const addressParts = [
    businessAddress.line1,
    businessAddress.line2,
    businessAddress.city,
    businessAddress.state,
    businessAddress.postcode,
    "United States",
  ].filter(Boolean);

  return addressParts.join(", ");
}

/**
 * Formats a business address with company name into a single string
 * @param businessAddress - Object containing address components
 * @param businessAddress.line1 - First line of street address
 * @param businessAddress.line2 - Optional second line of street address
 * @param businessAddress.city - City name
 * @param businessAddress.state - State code
 * @param businessAddress.postcode - Postal code
 * @param companyName - Name of the company
 * @param defaultValue - Value to display if address is incomplete (defaults to em dash)
 * @returns Formatted address string with company name or default value if incomplete
 */
export function formatAddressWithCompanyName(
  businessAddress: Maybe<{
    line1?: Maybe<string>;
    line2?: Maybe<string>;
    city?: Maybe<string>;
    state?: Maybe<string>;
    postcode?: Maybe<string>;
  }>,
  companyName: Maybe<string>,
  defaultValue = "\u2014",
) {
  // Check if we have at least company name, line1 and state - bare minimum for a meaningful address
  if (!businessAddress?.line1 || !businessAddress?.state || !companyName) {
    return defaultValue;
  }

  const addressParts = [
    businessAddress.line1,
    businessAddress.line2,
    businessAddress.city,
    businessAddress.state,
    businessAddress.postcode,
    "United States",
  ].filter(Boolean);

  const companyPart = companyName ? `${companyName} - ` : "";

  return companyPart + addressParts.join(", ");
}

/**
 * Formats a contact location label with business name, account number, and address
 * @param businessName - The business name for the contact
 * @param accountNumber - The account number for the contact
 * @param businessAddress - Object containing address components
 * @param businessAddress.line1 - First line of street address
 * @param businessAddress.line2 - Optional second line of street address
 * @param businessAddress.city - City name
 * @param businessAddress.state - State code
 * @param businessAddress.postcode - Postal code
 * @param defaultValue - Value to display if required fields are missing (defaults to em dash)
 * @returns Formatted string with each part on a new line: "BusinessName\nAccountNumber\nAddress" or default value if incomplete
 */
export function formatContactLocationLabel(
  businessName: Maybe<string>,
  accountNumber: Maybe<string>,
  businessAddress: Maybe<{
    line1?: Maybe<string>;
    line2?: Maybe<string>;
    city?: Maybe<string>;
    state?: Maybe<string>;
    postcode?: Maybe<string>;
  }>,
  defaultValue = "\u2014",
) {
  // Format the address
  const address = formatAddress(businessAddress, "");

  // If we don't have at least a business name or address, return default
  if (!businessName?.trim() && !address) {
    return defaultValue;
  }

  // Build the parts array
  const parts: string[] = [];

  // Add business name if available
  if (businessName?.trim()) {
    parts.push(businessName.trim());
  }

  // Add account number if available
  if (accountNumber?.trim()) {
    parts.push(accountNumber.trim());
  }

  // Add address if available
  if (address) {
    parts.push(address);
  }

  // If we have no parts, return default
  if (parts.length === 0) {
    return defaultValue;
  }

  return parts.join(", \n");
}

/**
 * Formats a nullable value with a fallback
 * @param value - The value to display, may be null/undefined
 * @param defaultValue - Value to show if value is null/undefined (defaults to em dash)
 * @returns Original value if present, otherwise the default value
 */
export function formatNullableValue(value: Maybe<string>, defaultValue = "\u2014") {
  return value ?? defaultValue;
}

/**
 * Formats a nullable or empty value with a fallback
 * @param value - The value to display, may be null/undefined/empty string
 * @param defaultValue - Value to show if value is null/undefined/empty (defaults to em dash)
 * @returns Original value if present and non-empty, otherwise the default value
 *
 * @example
 * // Handle null/undefined/empty string
 * formatEmptyableValue(null) // => "—"
 * formatEmptyableValue("") // => "—"
 * formatEmptyableValue("hello") // => "hello"
 * formatEmptyableValue(" hello ") // => " hello "
 */
export function formatEmptyableValue(value: Maybe<string>, defaultValue = "\u2014") {
  if (!value) return defaultValue;
  return value.trim() === "" ? defaultValue : value;
}

/**
 * Formats a phone number into a standardized national format
 * @param phone - The phone number to format, may be null/undefined
 * @param defaultValue - Value to show if phone is null/undefined/whitespace (defaults to em dash)
 * @returns Formatted phone number in national format, original string if parsing fails,
 *          or default value if invalid input
 */
export const formatPhoneNumber = (phone: Maybe<string>, defaultValue = "\u2014"): string => {
  if (!phone || !phone.trim()) return defaultValue;

  try {
    // For non-international numbers, assume US
    const phoneNumber = parsePhoneNumberWithError(phone, { defaultCountry: "US" });
    return phoneNumber.formatNational();
  } catch {
    return phone;
  }
};

/**
 * Formats an enum value into a human-readable label by converting from
 * SCREAMING_SNAKE_CASE to Title Case.
 *
 * @param value - The enum value to format (e.g. "CUSTOM_FEEDER")
 * @returns The formatted label (e.g. "Custom Feeder")
 *
 * @example
 * // Convert enum to readable label
 * formatEnumLabel("CUSTOM_FEEDER") // => "Custom Feeder"
 *
 * // Handle single word enums
 * formatEnumLabel("SUPPLIER") // => "Supplier"
 *
 * // Handle multiple underscores
 * formatEnumLabel("VERY_LONG_ENUM_NAME") // => "Very Long Enum Name"
 */
export const formatEnumLabel = (value: string): string => {
  return value
    .toLowerCase()
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Normalizes spacing in a string by trimming leading/trailing whitespace
 * and replacing multiple consecutive spaces with a single space.
 *
 * @param text - The string to normalize
 * @returns The normalized string with consistent spacing
 *
 * @example
 * // Removes leading/trailing spaces
 * normaliseSpacing("  hello  ") // => "hello"
 *
 * // Collapses multiple spaces
 * normaliseSpacing("hello    world") // => "hello world"
 */
export const normaliseSpacing = (name: string): string => {
  return name.trim().replace(/\s+/g, " ");
};

/**
 * Formats a number with comma separators, without decimal places.
 *
 * @param value - The number to format
 * @param fallback - Optional fallback value for null/undefined (defaults to em dash)
 * @returns Formatted number string with commas or fallback value
 *
 * @example
 * formatInteger(1000) // => "1,000"
 * formatInteger("5280") // => "5,280"
 * formatInteger(null) // => "—"
 * formatInteger(null, "N/A") // => "N/A"
 */
export const formatInteger = (value: string | number | null | undefined, fallback: string = "\u2014") => {
  if (value == null) return fallback;
  return currency(value, {
    symbol: "",
    precision: 0,
  }).format();
};
